import { Disclosure, Menu, Transition } from '@headlessui/react';
import { Bars3Icon, BellIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Fragment, useContext, useEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Pharmacy, route, SHOW_CUSTOMER_BANNER } from '../constant/constant';
import { PatientSubRole, ROLE, UserType } from '../interface/interface';
import MoniveaRoadLogo from './HeaderLogo';
import MobileNavBar from './MobileNavBar';
import { UserContext } from '../context/userContext';
import instance from '../config/axios';
import { useAuth } from 'react-oidc-context';
import { ArrowDownIcon, SupportIcon } from '../assets/svgImages';
import { ErxStatusBanner } from './ErxStatusBanner';
import { useSubNavItems } from '../hooks/useSubNavItems';
import SubNavBar from './SubNavBar';
import ShoppingCart from './ShoppingCart';
import { WelcomeBanner } from './WelcomeBanner';
import { CartContext } from '../context/cartContext';

const NavBar = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const auth = useAuth();
  const location = useLocation();
  const [navigation, setNavigation] = useState<UserType[]>([]);
  const [activeNav, setActiveNav] = useState<number | null>(null);
  const [hideBanner, setHideBanner] = useState(false);
  const [dropdownLink, setDropdownLink] = useState<UserType[]>([]);
  const [isPatient, setIsPatient] = useState<boolean>();
  const [patientRole, setPatientRole] = useState<PatientSubRole>();
  const menuRef = useRef<HTMLDivElement>(null);
  const { cart } = useContext(CartContext);
  const {
    hideDropdown,
    setHideDropdown,
    isMobile,
    userDetail,
    hasTokenRecords,
    iseRxDown,
    unReadItems,
    nativeView,
  } = useContext(UserContext);
  const isGuest = location.pathname.includes('/guest');

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setHideDropdown(true);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isMobile || isGuest) {
      setHideBanner(true);
      return;
    }
    const bannerState = localStorage.getItem('hideBanner_v2');
    if (bannerState) {
      const hide = SHOW_CUSTOMER_BANNER ? false : JSON.parse(bannerState);
      setHideBanner(hide);
    }
  }, []);

  const handleDropdownOpen = (item: UserType[]) => {
    setDropdownLink(item);
    setHideDropdown(false);
  };

  const handleBannerClose = () => {
    setHideBanner(true);
    localStorage.setItem('hideBanner_v2', JSON.stringify(true));
  };

  const handleBannerOpen = () => {
    if (isMobile) {
      window.location.href = 'tel: 1800 123 687';
      return;
    }
    setHideBanner(false);
    localStorage.setItem('hideBanner_v2', JSON.stringify(false));
  };

  const isSubNavActive = (subNavItems: UserType[]) => {
    return subNavItems.some(
      (subNavItem) => location.pathname === subNavItem.href,
    );
  };

  useEffect(() => {
    if (userDetail === undefined) return;
    setNavigation((route as any)[userDetail.role]);
    setIsPatient(userDetail?.role === ROLE.Patients);
    setPatientRole(
      userDetail.internal
        ? PatientSubRole.InternalPatient
        : PatientSubRole.ExternalPatient,
    );
  }, [userDetail]);

  const subNavItems = useSubNavItems(dropdownLink, patientRole);

  const RenderInitial = () => {
    return (
      <span className="relative inline-flex h-10 w-10 items-center justify-center rounded-full bg-secondary-pink-200">
        <span
          className={`relative font-bold text-base leading-none ${
            process.env.VITE_THEME_TOGGLE === Pharmacy.MEDRELEAF
              ? 'text-white'
              : 'text-primary-blue-500'
          }`}
        >
          {userDetail?.user_initial}
        </span>

        {/* Need to uncomment once app goes to live */}
        {Boolean(unReadItems) && nativeView && (
          <div className="absolute -right-1 top-2.5 inline-block -translate-y-full -translate-x-0.5 rounded-full bg-primary-red-300 p-1.5 text-xs" />
        )}
      </span>
    );
  };

  const userRecord = {
    name: 'Tom Cook',
    email: 'tom@example.com',
    imageUrl:
      'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
  };

  const userNavigation = [{ name: 'Sign out', href: '/logout' }];

  if (userDetail && userDetail.role === ROLE.Patients) {
    userNavigation.splice(2, 0, { name: 'Your Profile', href: '/profile' });
    const signOut = userNavigation.splice(
      userNavigation.findIndex((item) => item.name === 'Sign out'),
      1,
    )[0];
    // Need to uncomment once app goes to live
    if (nativeView) {
      userNavigation.splice(1, 0, {
        name: 'Inbox',
        href: '/notification-inbox',
      });
    }
    userNavigation.push(signOut);
  }

  const stopImpersonating = async () => {
    instance({
      method: 'GET',
      url: '/api/v1/user/impersonate_end',
      headers: {
        Authorization: `Bearer ${user?.access_token}`,
      },
      withCredentials: true,
    })
      .then((resp) => {
        if (resp.status === 200) {
          localStorage.removeItem('impersonateUser');
          auth.removeUser();
          navigate('/home');
        }
      })
      .catch((e: unknown) => {
        console.error('D> error fetching ending impersonation: ', e);
      });
  };

  function getSubNavHandler(item: UserType) {
    if (!item.subNav) return undefined;
    if (hideDropdown && isPatient) {
      return () => handleDropdownOpen(item?.subNav!);
    }
    return () => setHideDropdown(true);
  }

  return (
    <Disclosure
      ref={menuRef}
      as="nav"
      className={`${process.env.VITE_THEME_TOGGLE === Pharmacy.MEDRELEAF ? 'bg-white shadow-2xl' : 'bg-primary-blue-500'}  navbar`}
    >
      {({ open }) => (
        <>
          <div className="mx-auto">
            <div
              className={`${userDetail ? 'p-5' : 'py-7 px-5'} 3xl:p-10 items-center 
              justify-between header-container grid grid-cols-2 lg:grid-cols-3 gap-3`}
            >
              <div className="hidden lg:flex items-center">
                <div className="hidden md:block">
                  <div className="flex items-baseline space-x-4">
                    {navigation
                      ?.filter((x) => !x.mobileOnly)
                      ?.map((item: UserType, index: number) => (
                        <div
                          key={index}
                          onMouseEnter={() => setActiveNav(index)}
                          onMouseLeave={() => setActiveNav(null)}
                        >
                          <NavLink
                            onClick={getSubNavHandler(item)}
                            to={item.href}
                            className={({ isActive }) =>
                              `text-secondary-pink-100 fill-secondary-pink-100 lg:px-1 xl:px-2 py-1 2xl:text-base
                           hover:text-secondary-pink-300 hover:fill-secondary-pink-300 font-semibold relative 2.5xl:px-3 3xl:text-xl flex items-center justify-center align-middle'
                          ${
                            isSubNavActive(item.subNav || [])
                              ? 'text-secondary-pink-300 fill-secondary-pink-300'
                              : 'text-secondary-pink-100 fill-secondary-pink-100'
                          } ${isActive ? 'text-secondary-pink-300 fill-secondary-pink-300' : 'text-secondary-pink-100 fill-secondary-pink-100'}
                        `
                            }
                          >
                            <span className="flex align-middle items-center">
                              {item.name}{' '}
                            </span>
                            {item.name.toLowerCase() === 'tokens' &&
                              hasTokenRecords && (
                                <span className="absolute bg-secondary-pink-300 h-2.5 w-2.5 rounded-full top-0 right-0"></span>
                              )}
                            {item.subNav && (
                              <ArrowDownIcon
                                className={`${hideDropdown ? `rotate-0` : `rotate-180`} w-3 h-3 ml-2.5`}
                              />
                            )}
                          </NavLink>
                          {!isPatient && item.subNav && activeNav === index && (
                            <div
                              className="absolute bg-white rounded-md shadow p-1 z-50"
                              onMouseEnter={() => setActiveNav(index)}
                              onMouseLeave={() => setActiveNav(null)}
                            >
                              {item.subNav.map((subNavItem, subIndex) => (
                                <NavLink
                                  key={subIndex}
                                  to={subNavItem.href}
                                  className={`block px-4 py-2 text-sm text-primary-blue-500 font-semibold 
                                  hover:bg-secondary-pink-100 hover:rounded 
                                  ${process.env.VITE_THEME_TOGGLE === Pharmacy.MEDRELEAF ? 'hover:text-white' : 'hover:text-primary-blue-500'}`}
                                >
                                  {subNavItem.name}
                                </NavLink>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                </div>
              </div>
              <div
                id="moniveaRoadLogo"
                className="flex justify-start lg:justify-center"
              >
                <MoniveaRoadLogo
                  userToken={
                    user?.access_token ? user?.access_token : undefined
                  }
                  isGuest={isGuest}
                />
              </div>
              <div className="flex justify-end">
                <div className="ml-4 flex items-center md:ml-6">
                  {localStorage.getItem('impersonateUser') && !isGuest && (
                    <button
                      type="button"
                      className="relative rounded-md bg-primary-red-400 p-3 text-white font-semibold text-sm mr-5 md:block hidden"
                      onClick={() => {
                        stopImpersonating();
                      }}
                    >
                      Stop Impersonating
                      <span className="sr-only">Stop Impersonating</span>
                    </button>
                  )}
                  <button
                    type="button"
                    className="relative rounded-full bg-indigo-600 p-1 text-indigo-200 hover:text-white 
                    focus:outline-none focus:ring-2 
                    focus:ring-white focus:ring-offset-2 focus:ring-offset-indigo-600 hidden"
                  >
                    <span className="absolute -inset-1.5" />
                    <span className="sr-only">View notifications</span>
                    <BellIcon className="h-6 w-6" aria-hidden="true" />
                  </button>

                  {isMobile &&
                  (auth.user?.profile?.groups as Array<string>)?.includes(
                    ROLE.Patients,
                  ) ? (
                    <div className="mr-1">
                      <ShoppingCart />
                    </div>
                  ) : (
                    <></>
                  )}

                  {hideBanner && (
                    <div onClick={handleBannerOpen}>
                      <SupportIcon className="w-6 cursor-pointer mr-1" />
                    </div>
                  )}

                  {/* Profile dropdown */}
                  {userDetail?.name && (
                    <Menu as="div" className="relative ml-3 flex items-center">
                      <div className="relative">
                        <Menu.Button className="relative flex  shadow-none items-center rounded-full bg-indigo-600 text-sm">
                          <span className="absolute -inset-1.5" />
                          <span className="sr-only">Open user menu</span>
                          <RenderInitial />
                        </Menu.Button>
                        <Transition
                          as={Fragment}
                          enter="transition ease-out duration-100"
                          enterFrom="transform opacity-0 scale-95"
                          enterTo="transform opacity-100 scale-100"
                          leave="transition ease-in duration-75"
                          leaveFrom="transform opacity-100 scale-100"
                          leaveTo="transform opacity-0 scale-95"
                        >
                          <Menu.Items
                            className="absolute right-0 top-full z-40 mt-2 w-48 origin-top-right 
                          rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                          >
                            {userNavigation.map((item) => (
                              <Menu.Item key={item.name}>
                                {({ active }) => (
                                  <a
                                    href={item.href}
                                    className={` ${
                                      active ? 'bg-gray-100' : ''
                                    } block px-4 py-2 text-sm text-gray-700 flex justify-between items-center`}
                                  >
                                    <span>{item.name}</span>
                                    {item.name === 'Inbox' && unReadItems && (
                                      <span className="z-10 inline-block rotate-0 skew-x-0 skew-y-0 scale-x-100 scale-y-100 rounded-full bg-primary-red-300 p-1 text-xs" />
                                    )}
                                  </a>
                                )}
                              </Menu.Item>
                            ))}
                          </Menu.Items>
                        </Transition>
                      </div>
                      <p className="hidden lg:flex text-secondary-pink-100 2xl:text-base 3xl:text-xl pl-3.5">
                        hi&nbsp;<span>{userDetail?.first_name}</span>
                      </p>
                    </Menu>
                  )}
                </div>
              </div>
              <div className="-mr-2 hidden">
                <Disclosure.Button
                  className="relative inline-flex items-center 
                justify-center rounded-md bg-indigo-600 p-2 text-indigo-200 hover:bg-indigo-500 
                hover:bg-opacity-75 hover:text-white focus:outline-none focus:ring-2 focus:ring-white 
                focus:ring-offset-2 focus:ring-offset-indigo-600"
                >
                  <span className="absolute -inset-0.5" />
                  <span className="sr-only">Open main menu</span>
                  {open ? (
                    <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                  ) : (
                    <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                  )}
                </Disclosure.Button>
              </div>
            </div>
          </div>

          <Disclosure.Panel className="hidden">
            <div className="space-y-1 px-2 pb-3 pt-2 sm:px-3">
              {navigation?.map((item: UserType) => (
                <Disclosure.Button
                  key={item.name}
                  as="a"
                  href={item.href}
                  className={` ${
                    item.href === location.pathname
                      ? 'bg-indigo-700 text-white'
                      : 'text-white hover:bg-indigo-500 hover:bg-opacity-75'
                  } block rounded-md px-3 py-2 text-base font-medium`}
                  aria-current={item.current ? 'page' : undefined}
                >
                  {item.name}
                </Disclosure.Button>
              ))}
            </div>
            <div className="border-t border-indigo-700 pb-3 pt-4">
              <div className="flex items-center px-5">
                <div className="flex-shrink-0">
                  <RenderInitial />
                </div>
                <div className="ml-3">
                  <div className="text-base font-medium text-white">
                    {userRecord.name}
                  </div>
                  <div className="text-sm font-medium text-indigo-300">
                    {userRecord.email}
                  </div>
                </div>
                <button
                  type="button"
                  className="relative ml-auto flex-shrink-0 rounded-full bg-indigo-600 p-1 text-indigo-200 hover:text-white focus:outline-none 
                  focus:ring-2 focus:ring-white focus:ring-offset-2 
                  focus:ring-offset-indigo-600"
                >
                  <span className="absolute -inset-1.5" />
                  <span className="sr-only">View notifications</span>
                  <BellIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
              <div className="mt-3 space-y-1 px-2">
                {userNavigation?.map((item) => (
                  <Disclosure.Button
                    key={item.name}
                    as="a"
                    href={item.href}
                    className="block rounded-md px-3 py-2 text-base font-medium text-white hover:bg-indigo-500 hover:bg-opacity-75"
                  >
                    {item.name}
                  </Disclosure.Button>
                ))}
              </div>
            </div>
          </Disclosure.Panel>

          <div className="block md:hidden flex justify-center">
            {localStorage.getItem('impersonateUser') && !isGuest && (
              <button
                type="button"
                className="flex items-center justify-center p-3 rounded-md bg-primary-red-400 text-white font-semibold text-sm w-11/12 mx-5 mb-5"
                onClick={() => {
                  stopImpersonating();
                }}
              >
                Stop Impersonating
                <span className="sr-only">Stop Impersonating</span>
              </button>
            )}
          </div>

          {isGuest ? (
            <></>
          ) : (
            <MobileNavBar
              nav={navigation}
              cartLineItemsExists={cart?.line_items?.length}
            />
          )}

          {process.env.VITE_SHOW_BANNER === 'TRUE' && !isMobile && (
            <WelcomeBanner
              hideBanner={hideBanner}
              handleBannerClose={handleBannerClose}
            />
          )}

          <SubNavBar
            items={subNavItems}
            isVisible={isPatient && !hideDropdown}
            isMobile={false}
          />

          {iseRxDown && <ErxStatusBanner />}
        </>
      )}
    </Disclosure>
  );
};

export default NavBar;
